<template>
	<div>
		<div class="xs-12 bgWarning--l4 b2 bcWarning c1 p4 r2 mb4">
			<div class="lh125">{{ $t("commission_calculator_text") }}</div>
		</div>

		<j-card class="bg5 mb4">
			<!-- Headline -->
			<div class="xs-12 flex wrap xs-between bg4--l5 p4 bb1 bc7 radius-top mb2">
				<div class="fs4 lh125 c6 fw7">{{ $t("commission_calculator") }}</div>
			</div>

			<div class="p4">
				<div class="flex xs-between xs-middle mb2">
					<div class="fs3 c6 fw7">{{ $t("select_function_template") }}</div>
					<j-button
						size="small"
						type="tertiary"
						@click="$modal.show('createFunctionTemplate')"
						>{{ $t("create_function_template") }}</j-button
					>
				</div>

				<j-base-select
					class="mb4"
					v-model="selectedFunctionTemplate"
					:options="listOfTemplates"
				/>

				<div class="fs3 mb2 c6 fw7">
					{{ $t("output") }}
				</div>
				<div v-for="(item, index) in generatedOutput" :key="index" class="mb2">
					<p class="mb1 fs3 c6">{{ item.var }}</p>
					<div class="flex xs-betwween xs-middle">
						<div
							class="input-value xs r2 pl2 pr2 pt3 pb3 cu2 b1 bc7"
							@click="copyToClipboard(item.valueJsonString)"
						>
							<span>{{ item.valueJsonString || "---" }}</span>
						</div>
					</div>
				</div>

				<div
					v-if="generatedOutput.length === 0"
					class="flex xs-betwween xs-middle mb4"
				>
					<div class="input-value xs r2 pl2 pr2 pt3 pb3 cu2 b1 bc7">
						<span class="c6 fs3">{{ $t("commission_calculator_text_2") }}</span>
					</div>
				</div>

				<div class="flex xs-right">
					<j-button @click="renderFunctionTemplate">{{
						$t("calculate")
					}}</j-button>
				</div>
			</div>
		</j-card>

		<apeiron-commission-split-and-summary
			:policyId="$route.params.id"
			:renderOutputs="generatedOutput"
		/>

		<core-modal
			name="createFunctionTemplate"
			:headline="$t('create_function_template')"
		>
			<function-template-create
				@created:functionTemplate="$modal.hide('createFunctionTemplate')"
			/>
		</core-modal>
	</div>
</template>

<script>
import ApeironCommissionSplitAndSummary from "./ApeironCommissionSplitAndSummary"
import JBaseSelect from "@/components/JBaseSelect/"
import JButton from "@/components/JButton/"
import JCard from "@/components/JCard/"
import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"

import CoreModal from "@/modules/core/components/CoreModal.vue"
import FunctionTemplateCreate from "./FunctionTemplateCreate"

export default {
	components: {
		ApeironCommissionSplitAndSummary,
		JBaseSelect,
		JButton,
		JCard,
		CoreModal,
		FunctionTemplateCreate
	},

	data() {
		return {
			selectedFunctionTemplate: null,
			listOfTemplates: null,
			generatedOutput: []
		}
	},

	methods: {
		async fetchListOfFunctionTemplates() {
			const query = gql`
				query templates($where: templateWhereInput) {
					templates(where: $where) {
						list {
							id
							name
							... on functionTemplate {
								engine {
									name
								}
							}
						}
					}
				}
			`
			const variables = {
				where: {
					type: "function"
				}
			}

			const res = await api({ query, variables })

			if (!res?.data?.templates?.list) {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.errors ||
							res?.data?.templates?.errors ||
							"Oops ! An error occured."
					}
				})
			}

			this.listOfTemplates = res?.data?.templates?.list?.map(item => ({
				name: `${item.name} - ${item?.engine?.name}`,
				value: item.id
			}))
		},

		async renderFunctionTemplate() {
			const query = gql`
				query renderFunctionTemplate(
					$templateId: String!
					$input: templateRenderParametersInput!
				) {
					renderFunctionTemplate(templateId: $templateId, input: $input) {
						value {
							name
							var
							valueJsonString
						}
					}
				}
			`
			const variables = {
				templateId: this.selectedFunctionTemplate,
				input: {
					name: "data",
					variablesJsonString: JSON.stringify({
						where: { id: this.$route.params.id }
					})
				}
			}

			const res = await api({ query, variables })

			if (!res?.data?.renderFunctionTemplate?.value) {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.errors ||
							res?.data?.renderFunctionTemplate?.errors ||
							"Oops ! An error occured."
					}
				})
			}

			const obj = res?.data?.renderFunctionTemplate?.value

			JSON.stringify(obj, null, "\t") // stringify with tabs inserted at each level
			JSON.stringify(obj, null, 4) // stringify with 4 spaces at each level

			this.generatedOutput = obj
		},

		copyToClipboard(str) {
			// Helper to save value to clipboard
			//  Not guaranteed to work everywhere, works in Chrome
			const copyToClipboard = str => {
				const el = document.createElement("textarea")
				el.value = str
				el.setAttribute("readonly", "")
				el.style.position = "absolute"
				el.style.left = "-9999px"
				document.body.appendChild(el)
				el.select()
				document.execCommand("copy")
				document.body.removeChild(el)
			}

			// Save result to clipboard
			copyToClipboard(str)

			// Show message
			this.$store.dispatch("addMessage", {
				delay: 1000,
				message: {
					type: "neutral",
					text: "Copied to your clipboard!"
				}
			})
		}
	},

	computed: {
		stringifiedOutput() {
			return JSON.stringify(this.generatedOutput)
		}
	},

	created() {
		this.fetchListOfFunctionTemplates()
	}
}
</script>
